import React from "react";
import { V2_ROUTES } from "../state/useV2Navigation";

/** Full sidebar / menu catalog (order = desktop sidebar). */
export const NAV_ITEMS = [
  {
    key: V2_ROUTES.home,
    label: "Ask",
    shortLabel: "Ask",
    title: "Assistant",
  },
  {
    key: V2_ROUTES.addKnowledge,
    label: "Add knowledge",
    shortLabel: "Add",
    title: "Add knowledge — enrich an application or add content to your map",
  },
  {
    key: V2_ROUTES.insightsInbox,
    label: "Inbox",
    title: "Review uploads before they join your map",
  },
  {
    key: V2_ROUTES.baselineApprovals,
    label: "Approvals",
    title: "Approve items before they join your map",
  },
  {
    key: V2_ROUTES.architectureGallery,
    label: "Your map",
    shortLabel: "Map",
    title: "Browse business, apps, data, and technology on your map",
  },
  {
    key: V2_ROUTES.reports,
    label: "Reports",
    title: "Reports and insights — saved and shared report catalog",
    desktopOnly: true,
  },
  {
    key: V2_ROUTES.admin,
    label: "Admin",
    title: "Configure your map, approvals, users, and imports",
    desktopOnly: true,
  },
  {
    key: V2_ROUTES.help,
    label: "Help",
    title: "Menus, features, and product questions",
    desktopOnly: true,
  },
  {
    key: V2_ROUTES.settings,
    label: "Settings",
    title: "Personal settings — change password",
  },
];

/**
 * Sidebar utility cluster (pinned to the bottom of the desktop rail,
 * icon-only). Personal Settings deliberately does NOT live here — it sits
 * in the top panel next to Sign out (and in the mobile More sheet).
 */
export const SIDEBAR_UTILITY_KEYS = [V2_ROUTES.admin, V2_ROUTES.help];

/** Synthetic More-sheet action (not a route). */
export const MOBILE_ADD_KNOWLEDGE_ACTION = "add-knowledge";

/** Primary destinations on the mobile bottom bar. */
export const MOBILE_PRIMARY_KEYS = [
  V2_ROUTES.home,
  V2_ROUTES.addKnowledge,
  V2_ROUTES.insightsInbox,
  V2_ROUTES.architectureGallery,
  V2_ROUTES.baselineApprovals,
];

/** Overflow sheet destinations (plus Add knowledge + Sign out in the UI). */
export const MOBILE_MORE_KEYS = [V2_ROUTES.settings];

/** Routes that should not be used on mobile (nav + soft deep-link gate). */
export function isDesktopOnlyRoute(routeKey) {
  const item = NAV_ITEMS.find((i) => i.key === routeKey);
  return Boolean(item?.desktopOnly);
}

export function IconForRoute({ routeKey, active, tokens, className = "w-5 h-5" }) {
  const cls = `${className} ${active ? tokens.sidebar.iconActive : tokens.sidebar.iconIdle}`;
  if (routeKey === V2_ROUTES.home) {
    // Assistant: speech bubble with message dots.
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        <circle cx="8" cy="11.5" r="0.6" fill="currentColor" />
        <circle cx="12" cy="11.5" r="0.6" fill="currentColor" />
        <circle cx="16" cy="11.5" r="0.6" fill="currentColor" />
      </svg>
    );
  }
  if (routeKey === V2_ROUTES.addKnowledge || routeKey === MOBILE_ADD_KNOWLEDGE_ACTION) {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  if (routeKey === V2_ROUTES.insightsInbox) {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 6.5h16v11H4z" />
        <path d="M8 10.5h8" />
        <path d="M4 14h5l1.5 2h3L15 14h5" />
      </svg>
    );
  }
  if (routeKey === V2_ROUTES.baselineApprovals) {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    );
  }
  if (routeKey === V2_ROUTES.architectureGallery) {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3.5" y="4" width="7" height="7" />
        <rect x="13.5" y="4" width="7" height="7" />
        <rect x="3.5" y="13" width="7" height="7" />
        <rect x="13.5" y="13" width="7" height="7" />
      </svg>
    );
  }
  if (routeKey === V2_ROUTES.reports) {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 19h16" />
        <path d="M7 16V9" />
        <path d="M12 16V5" />
        <path d="M17 16v-6" />
      </svg>
    );
  }
  if (routeKey === V2_ROUTES.explorer) {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 5h16v14H4z" />
        <path d="M9 5v14" />
        <path d="M12 9h6" />
        <path d="M12 13h6" />
      </svg>
    );
  }
  if (routeKey === V2_ROUTES.admin) {
    // Admin: shield with a person — the Material "admin panel settings"
    // convention, instantly read as administration / privileged control.
    // (Settings keeps the cog; the two must not share an icon.)
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 22s8-3.6 8-10V5l-8-3-8 3v7c0 6.4 8 10 8 10z" />
        <circle cx="12" cy="10" r="2" />
        <path d="M8.8 16.2c.6-1.9 1.8-2.8 3.2-2.8s2.6.9 3.2 2.8" />
      </svg>
    );
  }
  if (routeKey === V2_ROUTES.help) {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9.5a2.5 2.5 0 1 1 3.8 2.1c-.8.5-1.3 1-1.3 2" />
        <path d="M12 16.5h.01" />
      </svg>
    );
  }
  if (routeKey === V2_ROUTES.settings) {
    // Personal settings: cog wheel.
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    );
  }
  if (routeKey === "more") {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
        <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}

export function inboxBellTitle({ pendingReviewCount, inboxNotificationCount }) {
  if (pendingReviewCount > 0) {
    return `Inbox — ${pendingReviewCount} upload${pendingReviewCount === 1 ? "" : "s"} need your OK`;
  }
  return `Inbox — ${inboxNotificationCount} notification${inboxNotificationCount === 1 ? "" : "s"}`;
}

export function approvalsBellTitle(pendingCount) {
  return `Approvals — ${pendingCount} notification${pendingCount === 1 ? "" : "s"} (pending items or submission updates)`;
}

export function formatNavBadgeCount(count) {
  return count > 99 ? "99+" : String(count);
}
